// v4 (V4-P2, spec-v4-structure-and-stage.md 5.2): the folds on a video's screen and where each one lives. On desktop
// the suite's side panel shows one tab at a time, and on phones a scene's folds sit in its detail sheet, so a fold
// the Director opens (the `fold` tool, ui.ts) may not be on screen yet: the project screen uses this map to switch to
// the right tab (or open the scene sheet) first. Fold ids are stable: other code opens them by id.

export type SuiteTab = "review" | "scene" | "cast" | "versions" | "brief";

/** Every fold id on the video screen, with a human name the Director can say. */
export const SUITE_FOLDS = {
  "scene.references": { tab: "scene", name: "References" },
  "scene.prompt": { tab: "scene", name: "Picture description" },
  "scene.model": { tab: "scene", name: "Motion and model" },
  "scene.sounds": { tab: "scene", name: "Sound list" },
  "video.versions": { tab: "versions", name: "Versions" },
  "project.history": { tab: "versions", name: "History" },
  "video.publish": { tab: "versions", name: "Publish" },
  "brief.look": { tab: "brief", name: "Look" },
  "brief.sources": { tab: "brief", name: "Sources" },
  "brief.photos": { tab: "brief", name: "Photos" },
} as const satisfies Record<string, { tab: SuiteTab; name: string }>;

export type SuiteFoldId = keyof typeof SUITE_FOLDS;

/** Bare section names (the `fold` tool's `panel`) that mean a place on the video screen without an exact fold. */
const BARE: Record<string, { tab: SuiteTab; id: SuiteFoldId | null }> = {
  details: { tab: "scene", id: null },
  scene: { tab: "scene", id: null },
  review: { tab: "review", id: null },
  brief: { tab: "brief", id: null },
  cast: { tab: "cast", id: null },
  // 2026-11-02: the "cast" tab is the video's World (characters, places, props); a video's places open there
  places: { tab: "cast", id: null },
  // the Suno pack's fold id carries the project id (music.suno.<pid>); it sits in the brief's Song panel
  suno: { tab: "brief", id: null },
};

/**
 * Where fold `id` lives on the video screen: its tab and its exact fold id (null when the name means a whole tab).
 * Takes an exact id ("scene.sounds"), the last part of one ("sounds") or a bare place ("details"). Null when the fold
 * isn't one of the video screen's (a Character or Settings fold).
 */
export function suiteFold(id: string): { tab: SuiteTab; id: SuiteFoldId | null } | null {
  const want = id.trim().toLowerCase();
  if (!want) return null;
  if (want in SUITE_FOLDS) return { tab: SUITE_FOLDS[want as SuiteFoldId].tab, id: want as SuiteFoldId };
  if (want.startsWith("music.suno.")) return { tab: "brief", id: null };
  if (!want.includes(".")) {
    const hit = (Object.keys(SUITE_FOLDS) as SuiteFoldId[]).find((k) => k.split(".").at(-1) === want);
    if (hit) return { tab: SUITE_FOLDS[hit].tab, id: hit };
    if (want in BARE) return BARE[want]!;
  }
  return null;
}

